import { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Mail, Building2, Save, AlertCircle, Lock, Trash2, DollarSign, ExternalLink, Database, QrCode, KeyRound, CalendarClock, Receipt, Bot, Pencil, Shield, ScrollText, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { EditUserDialog } from "@/components/EditUserDialog";
import RolesPermissions, { AuditLogCard } from "@/components/RolesPermissions";
import { usePermissions } from "@/hooks/usePermissions";
import { BackupPanel } from "@/components/BackupPanel";
import { MfaSecurityCard } from "@/components/auth/Mfa";
import { PaymentSettings } from "@/components/PaymentSettings";
import { DeletionLogCard } from "@/components/DeletionLogCard";
import { PayPalSettingsCard, ModeBadge } from "@/components/AutopaySettings";
import { AppointmentReminderSettings } from "@/components/AppointmentReminderSettings";
import { AppointmentContactSettings } from "@/components/AppointmentContactSettings";
import { ApiAccessSettings } from "@/components/api/ApiAccessSettings";
import { SchedulingSettings } from "@/components/SchedulingSettings";
import { GraphEmailSettings } from "@/components/GraphEmailSettings";
import { ExpenseSettings } from "@/components/ExpenseSettings";
import { AISettings } from "@/components/AISettings";
import { OverdueReminderSettings } from "@/components/OverdueReminderSettings";
import { QuoteAutomationSettings } from "@/components/QuoteAutomationSettings";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Globe } from "lucide-react";

const OnlineBookingCard = () => {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { axios.get(`${API}/settings/online-booking`).then((r) => setCfg(r.data)).catch(() => toast.error("Failed to load online booking settings")); }, []);
  const copy = (txt) => navigator.clipboard?.writeText(txt).then(() => toast.success("Link copied"));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings/online-booking`, { enabled: cfg.enabled, types: cfg.types, min_notice_hours: Number(cfg.min_notice_hours), horizon_days: Number(cfg.horizon_days), intro: cfg.intro || "" });
      setCfg(data); toast.success(data.enabled ? "Online booking is live" : "Online booking saved (off)");
    } catch (e) { const d = e.response?.data?.detail; toast.error(typeof d === "string" ? d : "Failed to save"); }
    finally { setSaving(false); }
  };
  if (!cfg) return null;
  const toggleType = (k) => setCfg({ ...cfg, types: cfg.types.includes(k) ? cfg.types.filter((x) => x !== k) : [...cfg.types, k] });
  return (
    <Card className="bg-white border-0 shadow-lg" data-testid="online-booking-card">
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div><CardTitle className="flex items-center gap-2"><Globe className="w-5 h-5 text-blue-700" />Online booking</CardTitle><CardDescription>Let customers request appointments themselves from a public page. Requests arrive unassigned with status "requested" and trigger the usual office alert.</CardDescription></div>
        <div className="flex items-center gap-2 shrink-0"><span className="text-sm text-slate-600">{cfg.enabled ? "On" : "Off"}</span><Switch checked={cfg.enabled} onCheckedChange={(v) => setCfg({ ...cfg, enabled: v })} data-testid="online-booking-enabled" /></div>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <Label>Bookable appointment types</Label>
          <div className="flex flex-wrap gap-3 mt-1">
            {cfg.all_types.map((t) => (
              <label key={t.key} className="flex items-center gap-2 text-sm rounded-lg border border-slate-200 px-3 py-2" data-testid={`online-booking-type-${t.key}`}>
                <Checkbox checked={cfg.types.includes(t.key)} onCheckedChange={() => toggleType(t.key)} data-testid={`online-booking-type-check-${t.key}`} />{t.label} <span className="text-xs text-slate-400">{t.duration} min</span>
              </label>
            ))}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label htmlFor="ob-notice">Minimum notice (hours)</Label><Input id="ob-notice" type="number" min={0} max={720} value={cfg.min_notice_hours} onChange={(e) => setCfg({ ...cfg, min_notice_hours: e.target.value })} data-testid="online-booking-notice" /></div>
          <div><Label htmlFor="ob-horizon">How far ahead (days)</Label><Input id="ob-horizon" type="number" min={1} max={365} value={cfg.horizon_days} onChange={(e) => setCfg({ ...cfg, horizon_days: e.target.value })} data-testid="online-booking-horizon" /></div>
        </div>
        <div><Label htmlFor="ob-intro">Intro text (optional)</Label><Textarea id="ob-intro" rows={2} value={cfg.intro || ""} onChange={(e) => setCfg({ ...cfg, intro: e.target.value })} placeholder="Tell customers what to expect…" data-testid="online-booking-intro" /></div>
        <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 space-y-2" data-testid="online-booking-links">
          <div className="flex items-center gap-2 text-sm"><code className="flex-1 truncate text-blue-900" data-testid="online-booking-public-url">{cfg.public_url}</code><Button size="sm" variant="outline" onClick={() => copy(cfg.public_url)} data-testid="online-booking-copy"><Copy className="w-4 h-4 mr-1" />Copy</Button></div>
          {cfg.types.map((k) => <div key={k} className="flex items-center gap-2 text-xs text-slate-600"><code className="flex-1 truncate">{cfg.public_url}?type={k}</code><Button size="sm" variant="ghost" onClick={() => copy(`${cfg.public_url}?type=${k}`)} data-testid={`online-booking-copy-${k}`}><Copy className="w-3.5 h-3.5" /></Button></div>)}
          <p className="text-[11px] text-slate-500">Add <code>&amp;name=&amp;email=&amp;company=</code> to prefill the form from email campaigns.</p>
        </div>
        <Button onClick={save} disabled={saving} className="bg-blue-700 hover:bg-blue-800" data-testid="online-booking-save"><Save className="w-4 h-4 mr-2" />{saving ? "Saving…" : "Save online booking"}</Button>
      </CardContent>
    </Card>
  );
};


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Settings() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [currentUser, setCurrentUser] = useState(null);
  const [users, setUsers] = useState([]);
  const [resettingPassword, setResettingPassword] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [userToEdit, setUserToEdit] = useState(null);
  const [passwordData, setPasswordData] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: ""
  });
  const [settings, setSettings] = useState({
    email_provider: "gmail",
    gmail_username: "",
    gmail_app_password: "",
    gmail_smtp_host: "smtp.gmail.com",
    gmail_smtp_port: "587",
    ms365_username: "",
    ms365_password: "",
    ms365_smtp_host: "smtp.office365.com",
    ms365_smtp_port: "587",
    graph_tenant_id: "",
    graph_client_id: "",
    graph_client_secret: "",
    graph_mailbox: "",
    sender_email: "",
    sender_name: "CG Networks & Co LLC",
    company_name: "CG Networks & Co LLC",
    company_address: "",
    company_phone: "",
    stripe_secret_key: "",
    stripe_webhook_secret: "",
    stripe_configured: false,
    cash_app_enabled: true,
    cash_app_cashtag: "cgnetworks",
    zelle_enabled: true,
    zelle_name: "CG NETWORKS & CO LLC",
    zelle_token: "cgomez@cgnetworks.cloud",
    payment_receipts_enabled: true,
    stripe_pay_page_enabled: true,
    stripe_fee_percent: 2.9,
    stripe_fee_fixed: 0.3
  });

  const { loaded: permsLoaded, me, can, canAny } = usePermissions();
  useEffect(() => {
    if (!permsLoaded) return;
    setCurrentUser(me);
    if (canAny("settings.manage", "settings.payments")) fetchSettings(); else setLoading(false);
    if (can("users.manage")) fetchUsers();
  }, [permsLoaded]); // eslint-disable-line

  const [roleNames, setRoleNames] = useState({});
  const roleName = (id) => roleNames[id];
  const fetchUsers = async () => {
    try {
      const [response, rolesRes] = await Promise.all([axios.get(`${API}/auth/users`), axios.get(`${API}/rbac/roles`)]);
      setUsers(response.data);
      setRoleNames(Object.fromEntries(rolesRes.data.map((r) => [r.id, r.name])));
    } catch (error) {
      console.error("Failed to fetch users", error);
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/email`);
      // CRITICAL FIX: Use fetched data as base, don't spread old state first
      // This prevents hardcoded default values from overriding fetched settings
      setSettings({
        ...response.data,
        email_provider: response.data.email_provider === 'microsoft365' ? 'graph' : response.data.email_provider,
        // Keep passwords/keys empty for security (backend will preserve existing values on save)
        gmail_app_password: "",
        ms365_password: "",
        graph_client_secret: "",
        stripe_secret_key: "",
        stripe_webhook_secret: ""
      });
    } catch (error) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/settings/email`, settings);
      if (response.data.success) {
        toast.success(response.data.message);
        
        // CRITICAL FIX: Refetch settings after save to get the actual saved values
        // This ensures the UI displays what was actually saved, not what was sent
        setTimeout(async () => {
          await fetchSettings();
          toast.success("Settings loaded successfully!");
        }, 1000);
        
        if (response.data.restarted) {
          toast.info("Settings are now active!", { duration: 3000 });
        }
      } else {
        toast.warning(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error("Please enter a test email address");
      return;
    }
    
    setTesting(true);
    try {
      const response = await axios.post(`${API}/settings/test-email?test_email=${encodeURIComponent(testEmail)}`);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send test email");
    } finally {
      setTesting(false);
    }
  };

  const handleChangePassword = async () => {
    // Validate fields
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error("Please fill in all password fields");
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error("New password must be at least 6 characters long");
      return;
    }

    setChangingPassword(true);
    try {
      const response = await axios.post(
        `${API}/auth/change-password?current_password=${encodeURIComponent(passwordData.currentPassword)}&new_password=${encodeURIComponent(passwordData.newPassword)}`
      );
      
      toast.success("Password changed successfully! Please log in again.");
      
      // Clear password fields
      setPasswordData({ currentPassword: "", newPassword: "", confirmPassword: "" });
      
      // Logout and redirect to login
      setTimeout(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        navigate('/login');
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  const handleAdminResetPassword = async (username) => {
    const newPassword = prompt(`Enter new password for ${username}:`);
    if (!newPassword) return;

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters long");
      return;
    }

    setResettingPassword(username);
    try {
      await axios.post(
        `${API}/auth/reset-password?username=${encodeURIComponent(username)}&new_password=${encodeURIComponent(newPassword)}`
      );
      toast.success(`Password reset successfully for ${username}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setResettingPassword(null);
    }
  };

  const handleDeleteUser = async () => {
    const username = userToDelete?.username;
    if (!username) return;
    setUserToDelete(null);
    setDeletingUser(username);
    try {
      await axios.delete(`${API}/auth/users/${encodeURIComponent(username)}`);
      toast.success(`User "${username}" was deleted`);
      fetchUsers(); // Refresh user list
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    } finally {
      setDeletingUser(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div data-testid="settings-page">
      <div className="mb-8">
        <h1 className="text-4xl font-bold text-slate-800 mb-2">Settings</h1>
        <p className="text-slate-600">{canAny("settings.manage", "users.manage") ? 'Configure system settings and manage users' : 'Manage your account settings'}</p>
      </div>

      <Tabs defaultValue="security" className="space-y-6">
        <div className="overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0 pb-1" data-testid="settings-tabs-scroll">
        <TabsList className="w-max min-w-full lg:min-w-0 lg:w-auto lg:h-auto lg:flex-wrap">
          <TabsTrigger value="security">
            <Lock className="w-4 h-4 mr-2" />
            Account Security
          </TabsTrigger>
          {can("users.manage") && (
              <TabsTrigger value="users" data-testid="users-tab">
                <Lock className="w-4 h-4 mr-2" />
                User Management
              </TabsTrigger>
          )}
          {can("roles.manage") && (
              <TabsTrigger value="roles" data-testid="roles-tab">
                <Shield className="w-4 h-4 mr-2" />
                Roles &amp; Permissions
              </TabsTrigger>
          )}
          {can("audit.view") && (
              <TabsTrigger value="audit" data-testid="audit-tab">
                <ScrollText className="w-4 h-4 mr-2" />
                Audit Log
              </TabsTrigger>
          )}
          {can("settings.manage") && (
            <>
              <TabsTrigger value="email">
                <Mail className="w-4 h-4 mr-2" />
                Email Configuration
              </TabsTrigger>
              <TabsTrigger value="company">
                <Building2 className="w-4 h-4 mr-2" />
                Company Information
              </TabsTrigger>
            </>
          )}
          {can("settings.payments") && (
            <>
              <TabsTrigger value="stripe">
                <DollarSign className="w-4 h-4 mr-2" />
                Stripe Integration
              </TabsTrigger>
              <TabsTrigger value="payments" data-testid="payments-tab">
                <QrCode className="w-4 h-4 mr-2" />
                Payments
              </TabsTrigger>
            </>
          )}
          {can("backup.manage") && (
              <TabsTrigger value="backup" data-testid="backup-tab">
                <Database className="w-4 h-4 mr-2" />
                Data Backup
              </TabsTrigger>
          )}
          {can("settings.manage") && (
            <>
              <TabsTrigger value="scheduling" data-testid="scheduling-tab">
                <CalendarClock className="w-4 h-4 mr-2" />
                Scheduling
              </TabsTrigger>
              <TabsTrigger value="expenses" data-testid="expenses-settings-tab">
                <Receipt className="w-4 h-4 mr-2" />
                Expenses
              </TabsTrigger>
            </>
          )}
          {can("ai.manage") && (
              <TabsTrigger value="ai" data-testid="ai-settings-tab">
                <Bot className="w-4 h-4 mr-2" />
                AI &amp; Integrations
              </TabsTrigger>
          )}
          {can("api_keys.manage") && (
              <TabsTrigger value="api" data-testid="api-access-tab">
                <KeyRound className="w-4 h-4 mr-2" />
                API Access
              </TabsTrigger>
          )}
        </TabsList>
        </div>

        {can("roles.manage") && <TabsContent value="roles"><RolesPermissions /></TabsContent>}
        {can("audit.view") && <TabsContent value="audit"><AuditLogCard /></TabsContent>}

        {can("api_keys.manage") && (
          <TabsContent value="api">
            <ApiAccessSettings />
          </TabsContent>
        )}

        {can("settings.manage") && (
          <TabsContent value="expenses"><ExpenseSettings /></TabsContent>
        )}

        {can("ai.manage") && (
          <TabsContent value="ai"><AISettings /></TabsContent>
        )}

        {can("settings.manage") && (
          <TabsContent value="scheduling" className="space-y-6">
            <SchedulingSettings />
            <OnlineBookingCard />
            <AppointmentContactSettings />
          </TabsContent>
        )}

        {can("settings.payments") && (
          <TabsContent value="payments">
            <PaymentSettings settings={settings} onChange={setSettings} />
            {can("audit.view") && <DeletionLogCard />}
          </TabsContent>
        )}

        {can("backup.manage") && (
          <TabsContent value="backup">
            <BackupPanel />
          </TabsContent>
        )}

        <TabsContent value="security">
          <MfaSecurityCard />
          <Card className="bg-white border-0 shadow-lg mt-6">
            <CardHeader>
              <CardTitle>Change Password</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="current_password">Current Password *</Label>
                <Input
                  id="current_password"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                  placeholder="Enter current password"
                />
              </div>
              <div>
                <Label htmlFor="new_password">New Password *</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                  placeholder="Enter new password (min 6 characters)"
                />
              </div>
              <div>
                <Label htmlFor="confirm_password">Confirm New Password *</Label>
                <Input
                  id="confirm_password"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                  placeholder="Confirm new password"
                />
              </div>
              
              <div className="flex items-start gap-2 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="text-sm text-yellow-800">
                  <p className="font-semibold mb-1">Security Notice</p>
                  <p>After changing your password, you will be automatically logged out and need to log in again with your new password.</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleChangePassword} 
                  disabled={changingPassword}
                  className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  {changingPassword ? "Changing Password..." : "Change Password"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="users">
          <Card className="bg-white border-0 shadow-lg">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle>User Management</CardTitle>
              <Button size="sm" onClick={() => setUserToEdit("new")} className="bg-blue-700 hover:bg-blue-800" data-testid="add-user-button"><UserPlus className="w-4 h-4 mr-1" />Add user</Button>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left p-3 font-semibold text-gray-700">Username</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Full Name</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Email</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Role</th>
                      <th className="text-left p-3 font-semibold text-gray-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user) => (
                      <tr key={user.id} className={`border-b border-gray-100 hover:bg-gray-50 ${user.active === false ? "opacity-60" : ""}`} data-testid={`user-row-${user.username}`}>
                        <td className="p-3">{user.username}</td>
                        <td className="p-3">{user.full_name}</td>
                        <td className="p-3">{user.email}</td>
                        <td className="p-3">
                          <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                            user.role_id === 'owner' ? 'bg-purple-100 text-purple-700' :
                            user.role === 'admin' ? 'bg-blue-100 text-blue-700' : 
                            user.role === 'technician' ? 'bg-green-100 text-green-700' : 
                            'bg-gray-100 text-gray-700'
                          }`} data-testid={`user-role-${user.username}`}>
                            {roleName(user.role_id) || user.role.toUpperCase()}
                          </span>
                          {user.active === false && <span className="ml-2 px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-700" data-testid={`user-inactive-${user.username}`}>INACTIVE</span>}
                          {(user.grants?.length || user.denies?.length) ? <span className="ml-2 text-[10px] text-slate-500" title={`${user.grants?.length || 0} grant(s), ${user.denies?.length || 0} deny(s)`}>+{user.grants?.length || 0}/−{user.denies?.length || 0}</span> : null}
                        </td>
                        <td className="p-3">
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setUserToEdit(user)}
                              disabled={resettingPassword === user.username || deletingUser === user.username}
                              className="text-blue-700 hover:text-blue-800 hover:bg-blue-50"
                              data-testid={`edit-user-button-${user.username}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleAdminResetPassword(user.username)}
                              disabled={resettingPassword === user.username || deletingUser === user.username}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                            >
                              <Lock className="w-4 h-4 mr-1" />
                              {resettingPassword === user.username ? "Resetting..." : "Reset Password"}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setUserToDelete(user)}
                              data-testid={`delete-user-button-${user.username}`}
                              disabled={
                                user.username === currentUser?.username || 
                                deletingUser === user.username || 
                                resettingPassword === user.username
                              }
                              className={`${
                                user.username === currentUser?.username 
                                  ? "text-gray-400 cursor-not-allowed" 
                                  : "text-red-600 hover:text-red-700 hover:bg-red-50"
                              }`}
                              title={user.username === currentUser?.username ? "Cannot delete your own account" : "Delete user"}
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              {deletingUser === user.username ? "Deleting..." : "Delete"}
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {users.length === 0 && (
                <div className="text-center py-8 text-gray-500">
                  No users found
                </div>
              )}

              {userToEdit && <EditUserDialog user={userToEdit === "new" ? null : userToEdit} currentUser={currentUser} onClose={() => setUserToEdit(null)} onSaved={() => { setUserToEdit(null); fetchUsers(); }} />}

              <AlertDialog open={!!userToDelete} onOpenChange={(open) => !open && setUserToDelete(null)}>
                <AlertDialogContent data-testid="delete-user-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete user "{userToDelete?.username}"?</AlertDialogTitle>
                    <AlertDialogDescription>
                      {userToDelete?.full_name} ({userToDelete?.email}) will lose access immediately
                      {userToDelete?.role === 'technician' ? ' and their technician record will be removed' : ''}. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="delete-user-cancel-button">Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700" data-testid="delete-user-confirm-button">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete User
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="email">
          <Card className="bg-white border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Email SMTP Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <Label htmlFor="email_provider">Email Provider *</Label>
                <Select 
                  value={settings.email_provider} 
                  onValueChange={(value) => setSettings({ ...settings, email_provider: value })}
                >
                  <SelectTrigger data-testid="email-provider-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="gmail">Gmail</SelectItem>
                    <SelectItem value="graph" data-testid="email-provider-graph">Microsoft 365 (Graph API)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {settings.email_provider === 'gmail' && (
                <div className="space-y-4 p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-900">Gmail Configuration</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="gmail_username">Gmail Address *</Label>
                      <Input
                        id="gmail_username"
                        type="email"
                        value={settings.gmail_username}
                        onChange={(e) => setSettings({ ...settings, gmail_username: e.target.value })}
                        placeholder="your-email@gmail.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gmail_app_password">Gmail App Password *</Label>
                      <Input
                        id="gmail_app_password"
                        type="password"
                        value={settings.gmail_app_password}
                        onChange={(e) => setSettings({ ...settings, gmail_app_password: e.target.value })}
                        placeholder="xxxx xxxx xxxx xxxx"
                      />
                      <p className="text-xs text-slate-500 mt-1">
                        Get from: <a href="https://myaccount.google.com/apppasswords" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google App Passwords</a>
                      </p>
                    </div>
                    <div>
                      <Label htmlFor="gmail_smtp_host">SMTP Host</Label>
                      <Input
                        id="gmail_smtp_host"
                        value={settings.gmail_smtp_host}
                        onChange={(e) => setSettings({ ...settings, gmail_smtp_host: e.target.value })}
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="gmail_smtp_port">SMTP Port</Label>
                      <Input
                        id="gmail_smtp_port"
                        value={settings.gmail_smtp_port}
                        onChange={(e) => setSettings({ ...settings, gmail_smtp_port: e.target.value })}
                        placeholder="587"
                      />
                    </div>
                  </div>
                </div>
              )}

              {settings.email_provider === 'graph' && <GraphEmailSettings settings={settings} onChange={setSettings} />}

              <div className="space-y-4 p-4 bg-slate-50 rounded-lg">
                <h3 className="font-semibold text-slate-900">Sender Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="sender_email">Sender Email *</Label>
                    <Input
                      id="sender_email"
                      type="email"
                      value={settings.sender_email}
                      onChange={(e) => setSettings({ ...settings, sender_email: e.target.value })}
                      placeholder="noreply@cgnetworks.com"
                    />
                  </div>
                  <div>
                    <Label htmlFor="sender_name">Sender Name *</Label>
                    <Input
                      id="sender_name"
                      value={settings.sender_name}
                      onChange={(e) => setSettings({ ...settings, sender_name: e.target.value })}
                      placeholder="CG Networks & Co LLC"
                    />
                  </div>
                </div>
              </div>

              <div className="flex items-start gap-2 p-4 bg-purple-50 border border-purple-200 rounded-lg">
                <Mail className="w-5 h-5 text-purple-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-semibold mb-1 text-purple-900">Test Email Configuration</p>
                  <p className="text-sm text-purple-800 mb-2">Send a test email to verify your SMTP settings are working correctly.</p>
                  <div className="flex gap-2">
                    <Input
                      type="email"
                      placeholder="Enter test email address"
                      value={testEmail}
                      onChange={(e) => setTestEmail(e.target.value)}
                      className="flex-1"
                    />
                    <Button 
                      onClick={handleTestEmail}
                      disabled={testing}
                      variant="outline"
                      size="sm"
                      className="bg-purple-600 text-white hover:bg-purple-700"
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {testing ? "Sending..." : "Send Test"}
                    </Button>
                  </div>
                </div>
              </div>

              <AppointmentReminderSettings />
              <OverdueReminderSettings />
              <QuoteAutomationSettings />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="company">
          <Card className="bg-white border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Company Information</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="company_name">Company Name *</Label>
                <Input
                  id="company_name"
                  value={settings.company_name}
                  onChange={(e) => setSettings({ ...settings, company_name: e.target.value })}
                  placeholder="CG Networks & Co LLC"
                />
              </div>
              <div>
                <Label htmlFor="company_address">Company Address *</Label>
                <Input
                  id="company_address"
                  value={settings.company_address}
                  onChange={(e) => setSettings({ ...settings, company_address: e.target.value })}
                  placeholder="2900 NW 7th ST, Suite 302, Miami, FL, 33125"
                />
              </div>
              <div>
                <Label htmlFor="company_phone">Company Phone *</Label>
                <Input
                  id="company_phone"
                  value={settings.company_phone}
                  onChange={(e) => setSettings({ ...settings, company_phone: e.target.value })}
                  placeholder="768-970-0477"
                />
              </div>
              <div>
                <Label htmlFor="app_public_url">Public app URL (for "View &amp; Pay" links in emails)</Label>
                <Input
                  id="app_public_url"
                  value={settings.app_public_url || ""}
                  onChange={(e) => setSettings({ ...settings, app_public_url: e.target.value })}
                  placeholder={settings.app_public_url_detected || window.location.origin}
                  data-testid="app-public-url-input"
                />
                <p className="text-xs text-slate-500 mt-1">Leave blank to auto-detect ({settings.app_public_url_detected || window.location.origin}). Set it only if customers should use a custom domain.</p>
              </div>
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-800">
                  This information will appear on all invoices, work orders, and email communications.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {can("settings.payments") && (
          <TabsContent value="stripe">
            <Card className="bg-white border-0 shadow-lg">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <DollarSign className="w-6 h-6 text-purple-600" />
                      Stripe Integration
                    </CardTitle>
                    <p className="text-sm text-slate-600 mt-2">
                      Connect your Stripe account to automatically send invoices and collect payments
                    </p>
                  </div>
                  {settings.stripe_configured && (
                    <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm" data-testid="stripe-connected-pill">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      Connected · ••••{settings.stripe_key_last4} <ModeBadge mode={settings.stripe_mode} testId="stripe-settings-mode-badge" />
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-5 h-5 text-purple-600 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-purple-900 mb-2">How to get your Stripe API keys:</p>
                      <ol className="text-sm text-purple-800 space-y-1 list-decimal list-inside">
                        <li>Go to your <a href="https://dashboard.stripe.com/test/apikeys" target="_blank" rel="noopener noreferrer" className="underline font-medium hover:text-purple-900">Stripe Dashboard</a></li>
                        <li>Click on "Developers" → "API keys"</li>
                        <li>Copy the "Secret key" (starts with sk_test_ or sk_live_)</li>
                        <li>For webhooks, go to "Developers" → "Webhooks" → "Add endpoint"</li>
                        <li>Use URL: <code className="bg-purple-100 px-2 py-0.5 rounded text-xs">{window.location.origin}/api/stripe/webhook</code></li>
                        <li>Select events: invoice.sent, invoice.paid, invoice.payment_failed, checkout.session.completed, payment_intent.succeeded, payment_intent.payment_failed, setup_intent.succeeded, setup_intent.setup_failed, payment_method.detached</li>
                        <li>Copy the "Signing secret" (starts with whsec_)</li>
                      </ol>
                      <p className="text-xs text-purple-700 mt-2">Keys are encrypted at rest and never shown again after saving — leave a field blank to keep the saved value.</p>
                    </div>
                  </div>
                </div>

                <div>
                  <Label htmlFor="stripe_secret_key">Stripe Secret Key *</Label>
                  <Input
                    id="stripe_secret_key"
                    type="password"
                    value={settings.stripe_secret_key}
                    onChange={(e) => setSettings({ ...settings, stripe_secret_key: e.target.value })}
                    placeholder={settings.stripe_configured ? `Saved (••••${settings.stripe_key_last4}) — paste a new key to replace` : "sk_test_... or sk_live_..."}
                    className="font-mono text-sm"
                    data-testid="stripe-secret-key-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Your Stripe secret API key (keep this secure)
                  </p>
                </div>

                <div>
                  <Label htmlFor="stripe_webhook_secret">Stripe Webhook Secret (Optional)</Label>
                  <Input
                    id="stripe_webhook_secret"
                    type="password"
                    value={settings.stripe_webhook_secret}
                    onChange={(e) => setSettings({ ...settings, stripe_webhook_secret: e.target.value })}
                    placeholder={settings.stripe_webhook_configured ? "Saved — paste a new secret to replace" : "whsec_..."}
                    className="font-mono text-sm"
                    data-testid="stripe-webhook-secret-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Required for automatic status updates when customers pay
                  </p>
                  <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs" data-testid="stripe-webhook-status">
                    <span className={settings.stripe_webhook_last_event_at ? "text-green-700" : "text-slate-500"} data-testid="stripe-webhook-last-event">
                      Stripe webhook: {settings.stripe_webhook_last_event_at ? `last event received ${new Date(settings.stripe_webhook_last_event_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}${settings.stripe_webhook_last_event_type ? ` (${settings.stripe_webhook_last_event_type})` : ""}` : "no events received yet"}
                    </span>
                    {settings.stripe_webhook_last_failure_at && <span className="text-red-600" data-testid="stripe-webhook-last-failure">Last signature failure {new Date(settings.stripe_webhook_last_failure_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} — check the signing secret</span>}
                    <span className="text-slate-500" data-testid="stripe-reconcile-status">
                      Payment sync: {settings.stripe_reconcile_last_run_at ? `last checked ${new Date(settings.stripe_reconcile_last_run_at).toLocaleString("en-US", { timeZone: "America/New_York", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}` : "runs every 5 minutes"}{settings.stripe_reconcile_last_result?.recorded ? ` · ${settings.stripe_reconcile_last_result.recorded} recovered` : ""}
                    </span>
                  </div>
                </div>

                <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg space-y-3">
                  <div className="flex items-start gap-2">
                    <ExternalLink className="w-5 h-5 text-blue-600 mt-0.5" />
                    <div>
                      <p className="font-semibold text-blue-900 mb-2">What happens when you enable Stripe:</p>
                      <ul className="text-sm text-blue-800 space-y-1 list-disc list-inside">
                        <li>A "Send via Stripe" checkbox appears when creating invoices</li>
                        <li>Invoices are automatically created in your Stripe account</li>
                        <li>Customers receive professional email invoices from Stripe</li>
                        <li>Payments are processed securely by Stripe</li>
                        <li>Invoice status updates automatically when paid</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {!settings.stripe_configured && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start gap-2">
                      <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                      <div className="text-sm text-yellow-800">
                        <p className="font-semibold mb-1">Stripe Not Configured</p>
                        <p>Add your Stripe API keys above and click "Save Settings" to enable Stripe integration.</p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            <div className="mt-6"><PayPalSettingsCard /></div>
          </TabsContent>
        )}
      </Tabs>

      <div className="mt-6 flex justify-end">
        <Button 
          onClick={handleSave} 
          disabled={saving}
          className="bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 shadow-lg"
          data-testid="save-settings-button"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving..." : "Save Settings"}
        </Button>
      </div>
    </div>
  );
}
